import { Badge, cn, Label, Paragraph, PrescriptionBottleIcon } from '@quickmd-llc/design-system'
import type { MedicationStaffApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { formatNdc } from '../constants'

interface MedicationCardProps {
  medication: MedicationStaffApiOutContract
  isSelected: boolean
  onClick: (medication: MedicationStaffApiOutContract) => void
}

function getPrimaryCode(medication: MedicationStaffApiOutContract): string | null {
  if (medication.NationalDrugCode) return `NDC: ${formatNdc(medication.NationalDrugCode)}`
  if (medication.GenericCodeNumber) return `GCN: ${medication.GenericCodeNumber}`
  if (medication.AlternativeThirdPartyKey) return medication.AlternativeThirdPartyKey
  return null
}

function getPriceRange(medication: MedicationStaffApiOutContract): string | null {
  const prices = (medication.Formulations ?? []).map((f) => f.Price)
  if (prices.length === 0) return null
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  if (min === max) return `$${min.toFixed(2)}`
  return `$${min.toFixed(2)} – $${max.toFixed(2)}`
}

export function MedicationCard({ medication, isSelected, onClick }: MedicationCardProps) {
  const primaryCode = getPrimaryCode(medication)
  const priceRange = getPriceRange(medication)

  return (
    <button
      type='button'
      onClick={() => onClick(medication)}
      className={cn(
        'relative w-full rounded-medium p-sp-1 text-left transition-colors',
        isSelected ? 'bg-coastal-blue-10' : 'hover:bg-coastal-blue-10',
      )}>
      {medication.IsControlledSubstance ?
        <PrescriptionBottleIcon iconSize='m' className='absolute right-sp-2 top-sp-2 text-text-light' />
      : null}
      <Label size='md' className={cn(medication.IsControlledSubstance && 'line-clamp-3 pr-sp-4')}>
        {medication.Name}
      </Label>
      {primaryCode ?
        <Badge variant='idle' size='s' className='mt-sp-0.5'>
          {primaryCode}
        </Badge>
      : null}
      {priceRange ?
        <Paragraph size='sm' className='mt-sp-0.5 font-medium text-highlight'>
          {priceRange}
        </Paragraph>
      : null}
    </button>
  )
}
